import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import BreadcrumbComponent from "src/core/components/breadcrumb/breadcrumb.component";
import { BreadCrumbInfoModel } from "src/shared/models/breadCrumbInfo.model";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { getAssetByCode } from "src/store/slices/asset.slice";
import "./asset-configuration.scss";
import ContentHeader from "src/shared/content-header/content-header.component";
import general_config_icon from "../../../../../assets/images/general-config-icon.png";
import zones_icon from "../../../../../assets/images/zones-icon.png";
import profile_icon from "../../../../../assets/images/profile-icon.png";
import light_groups_icon from "../../../../../assets/images/light-groups-icon.png";
import daylight_profile_icon from "../../../../../assets/images/daylight-profile-icon.png";

type AssetConfigurationSection =
    | "general-configuration"
    | "zones"
    | "timer-profile"
    | "daylight-profile"
    | "light-groups";

const DESKTOP_MEDIA_QUERY = "(min-width: 1200px)";

const sidebarSections: Array<{
    id: AssetConfigurationSection;
    label: string;
    icon: string;
    link: string;
    hasChildren?: boolean;
}> = [
        {
            id: "general-configuration",
            label: "General Configuration",
            icon: general_config_icon,
            link: "general-configuration",
        },
        {
            id: "timer-profile",
            label: "Profile",
            icon: profile_icon,
            link: "timer-profile",
            hasChildren: true,
        },
        {
            id: "zones",
            label: "Zones",
            icon: zones_icon,
            link: "zones",
        },
        {
            id: "light-groups",
            label: "Light Groups",
            icon: light_groups_icon,
            link: "light-groups",
        },
    ];

const getIsDesktop = () =>
    typeof window !== "undefined" && window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

const AssetConfigurationComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const assetCode = params.id ? safeAtob(params.id) : "";

    const [isDesktop, setIsDesktop] = useState(getIsDesktop);
    const [isSidebarOpen, setIsSidebarOpen] = useState(getIsDesktop);
    const [selectedSection, setSelectedSection] =
        useState<AssetConfigurationSection>("general-configuration");

    const sidebarRef = useRef<HTMLElement>(null);
    const mobileTabRef = useRef<HTMLButtonElement>(null);

    const configBasePath = params.id ? `/asset/${params.id}/configuration` : "/asset";

    const assetDetails = useSelector((state: any) => {
        return state.asset.assetDetails;
    });
    const isGateway = assetDetails?.assetType?.toLowerCase() === "gateway";
    const visibleSidebarSections = isGateway
        ? sidebarSections
        : sidebarSections.filter((section) => section.id === "general-configuration");

    // Sync selected section with the route, and redirect bare "/configuration" to a default tab.
    useEffect(() => {
        if (assetCode) {
            dispatch(getAssetByCode({ id: assetCode }));
        }

        const normalizedPath = location.pathname.replace(/\/$/, "");

        if (normalizedPath.endsWith("/configuration")) {
            navigate(`${normalizedPath}/general-configuration`, { replace: true });
            return;
        }

        const matchedSection = sidebarSections
            .flatMap((section) =>
                section.id === "timer-profile"
                    ? ["timer-profile", "daylight-profile"]
                    : [section.id],
            )
            .find((id) => normalizedPath.includes(`/configuration/${id}`));

        if (matchedSection) {
            setSelectedSection(matchedSection as AssetConfigurationSection);
        }
    }, [assetCode, dispatch, location.pathname, navigate]);

    // Single source of truth for desktop/mobile: keep isDesktop in sync with the viewport,
    // and default the sidebar open/closed accordingly.
    useEffect(() => {
        const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

        const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
            setIsDesktop(event.matches);
            setIsSidebarOpen(event.matches);
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handleChange);
        } else {
            mediaQuery.addListener(handleChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener("change", handleChange);
            } else {
                mediaQuery.removeListener(handleChange);
            }
        };
    }, []);

    // Close the sidebar when clicking/tapping outside of it (mobile only).
    useEffect(() => {
        if (isDesktop || !isSidebarOpen) {
            return;
        }

        const handleOutsidePointer = (event: PointerEvent) => {
            const target = event.target as Node | null;

            if (!target) {
                return;
            }

            const clickedInsideSidebar = sidebarRef.current?.contains(target);
            const clickedToggleButton = mobileTabRef.current?.contains(target);

            if (!clickedInsideSidebar && !clickedToggleButton) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener("pointerdown", handleOutsidePointer);

        return () => {
            document.removeEventListener("pointerdown", handleOutsidePointer);
        };
    }, [isDesktop, isSidebarOpen]);

    const breadCrumbInfo = useMemo<BreadCrumbInfoModel[]>(() => {
        const base = [
            { label: "Asset", active: false, onClick: () => navigate("/asset") },
            {
                label: assetCode || "Asset",
                active: false,
                onClick: () => navigate(`/asset/${params.id}`),
            },
        ];

        if (selectedSection === "timer-profile" || selectedSection === "daylight-profile") {
            return [
                ...base,
                { label: "Profile", active: false },
                { label: selectedSection === "timer-profile" ? "Timer Profile" : "Daylight Profile", active: true },
            ];
        }

        const sectionLabel = sidebarSections.find((section) => section.id === selectedSection)?.label || "Configuration";

        return [...base, { label: sectionLabel, active: true }];
    }, [assetCode, navigate, params.id, selectedSection]);

    const goToSection = (section: AssetConfigurationSection, link: string) => {
        navigate(`${configBasePath}/${link}`);
        setSelectedSection(section);
        if (!isDesktop) {
            setIsSidebarOpen(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Configuration | SLP</title>
            </Helmet>

            <div>
                <div className="header-container">
                    <div className="title-container">
                        <ContentHeader pageTitle="Configuration" />
                    </div>
                </div>

                <div className="page">
                    <div className="page-container mt-4">
                        <div
                            className={`asset-configuration-layout ${isSidebarOpen ? "asset-configuration-layout--sidebar-open" : ""
                                }`}
                        >
                            <button
                                ref={mobileTabRef}
                                type="button"
                                className="asset-config-mobile-tab"
                                onClick={() => setIsSidebarOpen((current) => !current)}
                                aria-label={
                                    isSidebarOpen ? "Close configuration menu" : "Open configuration menu"
                                }
                                aria-expanded={isSidebarOpen}
                            >
                                <span className="asset-config-mobile-tab__label">Config</span>
                            </button>

                            {!isDesktop && isSidebarOpen ? (
                                <button
                                    type="button"
                                    className="asset-config-sidebar-backdrop"
                                    onClick={() => setIsSidebarOpen(false)}
                                    aria-label="Close configuration menu backdrop"
                                />
                            ) : null}

                            <aside ref={sidebarRef} className="asset-config-sidebar">
                                <div className="asset-config-sidebar-panel">
                                    <div className="asset-config-sidebar-title">
                                        Configuration Settings
                                    </div>

                                    <div className="asset-config-menu">
                                        {visibleSidebarSections.map((section) => {
                                            const isActive = selectedSection === section.id;

                                            if (section.hasChildren) {
                                                const isGroupExpanded =
                                                    selectedSection === "timer-profile" ||
                                                    selectedSection === "daylight-profile";

                                                return (
                                                    <div key={section.id} className="asset-config-menu-group">
                                                        <button
                                                            type="button"
                                                            className={`asset-config-menu-item asset-config-menu-item--group ${isGroupExpanded
                                                                ? "asset-config-menu-item--active rounded-3"
                                                                : ""
                                                                }`}
                                                            onClick={() => goToSection("timer-profile", section.link)}
                                                        >
                                                            <span className="asset-config-menu-item__meta">
                                                                <span className="asset-config-menu-icon">
                                                                    <img src={section.icon} alt="Profile menu" />
                                                                </span>
                                                                <span>{section.label}</span>
                                                            </span>
                                                            <i
                                                                className={`bi bi-chevron-down asset-config-menu-chevron ${isGroupExpanded
                                                                    ? "asset-config-menu-chevron--open"
                                                                    : ""
                                                                    }`}
                                                            />
                                                        </button>

                                                        {isGroupExpanded ? (
                                                            <div className="asset-config-menu-subgroup">
                                                                <button
                                                                    type="button"
                                                                    className={`asset-config-menu-item asset-config-menu-subitem ${selectedSection === "timer-profile"
                                                                        ? "asset-config-menu-subitem--active"
                                                                        : ""
                                                                        }`}
                                                                    onClick={() => goToSection("timer-profile", "timer-profile")}
                                                                >
                                                                    <img src={profile_icon} alt="Timer profile menu" />
                                                                    <span>Timer Profile</span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className={`asset-config-menu-item asset-config-menu-subitem ${selectedSection === "daylight-profile"
                                                                        ? "asset-config-menu-subitem--active"
                                                                        : ""
                                                                        }`}
                                                                    onClick={() =>
                                                                        goToSection("daylight-profile", "daylight-profile")
                                                                    }
                                                                >
                                                                    <img
                                                                        src={daylight_profile_icon}
                                                                        alt="Daylight profile menu"
                                                                    />
                                                                    <span>Daylight Profile</span>
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <button
                                                    type="button"
                                                    key={section.id}
                                                    className={`asset-config-menu-item ${isActive ? "asset-config-menu-item--active rounded-3" : ""
                                                        }`}
                                                    onClick={() => goToSection(section.id, section.link)}
                                                >
                                                    <span className="asset-config-menu-item__meta">
                                                        <span className="asset-config-menu-icon">
                                                            <img src={section.icon} alt={`${section.label} menu`} />
                                                        </span>
                                                        <span>{section.label}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </aside>

                            <main className="asset-configuration-content">
                                <div className="asset-configuration-breadcrumb my-4">
                                    <BreadcrumbComponent breadCrumbInfo={breadCrumbInfo} />
                                </div>
                                <Outlet />
                            </main>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AssetConfigurationComponent;